/**
 * Simplifying helpers for dragging files into the browser.
 *
 * Contains two functions: `installDragBackstop` to prevent all navigation due
 * to drops. Ensures a missed drop never navigates away from the app, and
 * provides the assumption that `dragHelper` makes.
 *
 * `dragHelper` accepts simplified event handlers, plus filter predicates
 * that apply in correlation to the appropriate underlying DOM events.
 *
 * @module
 */

/**
 * Installs the app-wide never-navigate rule. Call once, as early as
 * possible.
 *
 * This is a hard prerequisite for `dragHelper`: an uncancelled `dragover`
 * means the browser never fires `drop` at all -- it handles the drag
 * natively and navigates away. Without this, drop callbacks never run,
 * however they are bound.
 *
 * Bound to `window` so it also covers element targets: cancelling during
 * bubbling still counts, so an element's `drop` fires even though the
 * cancellation happens above it.
 *
 * `dragenter` is canceled too. Modern browsers only require `dragover`,
 * but cancelling both is the portable form and costs nothing.
 *
 * Deliberately returns no disposer: nothing should ever uninstall it.
 */
export function installDragBackstop() {
    const cancel = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragenter', cancel);
    window.addEventListener('dragover', cancel);
    window.addEventListener('drop', cancel);
}

/**
 * The type of the `target` option accepted by `dragHelper`.
 */
export interface DragEventTarget {
    // The slice of EventTarget this module needs. Declared structurally
    // rather than as `Window | HTMLElement`: that union collapses
    // addEventListener's overloads to the base
    // EventListenerOrEventListenerObject signature, so a `(e: DragEvent) =>
    // void` will not go in without a cast. Both `window` and any element
    // satisfy this as-is.
    addEventListener(type: string, listener: (event: DragEvent) => void): void;
    removeEventListener(
        type: string,
        listener: (event: DragEvent) => void
    ): void;
}

export type DragEventPredicate = (event: DragEvent) => boolean;

export interface DragHelperOptions {
    /**
     * `window` for "anywhere in the app", or an element for a scoped drop
     * zone. Required rather than defaulted: the scope of a drop target is a
     * decision worth stating at the call site.
     */
    target: DragEventTarget;
    /**
     * Cheap test for the information-limited drag phase, where only
     * `dataTransfer.types` is populated. Gates the enter/leave edge
     * callbacks.
     **/
    preFilter?: DragEventPredicate;
    /**
     * Full test when the drop is committed, when `dataTransfer.files` is
     * finally readable. Decides which of onDropAccepted / onDropRejected
     * the drop is reported to.
     */
    filter?: DragEventPredicate;
    dropEffect?: DataTransfer['dropEffect'];
    onFirstDragEnter?: (event: DragEvent) => void;
    onLastDragLeave?: (event: DragEvent) => void;
    /** A drop arrived and `filter` accepted it. */
    onDropAccepted?: (event: DragEvent) => void;
    /**
     * A drop arrived and `filter` turned it down. Defaults to
     * onLastDragLeave, because the teardown is identical -- undo whatever
     * onFirstDragEnter did.
     *
     * Supply it separately when the refusal deserves an explanation. A
     * leave means the user changed their mind and wants no feedback; a
     * rejected drop means they committed and were refused, which is usually
     * worth saying out loud ("only .json files are accepted"). The event is
     * passed so the message can name the offending file.
     */
    onDropRejected?: (event: DragEvent) => void;
    onDragEnd?: (event: DragEvent) => void;
}

const always = (_: DragEvent) => true;

/**
 * Registers file-drag listeners on `target` and returns a disposer.
 *
 * This is not a hook, so should be called inside a useEffect - the returned
 * disposer can be further returned to useEffect. But it could also just as
 * easily be called from componentDidMount.
 *
 * Requires `installDragBackstop()` to have been called; see above.
 *
 * Note that nothing here calls preventDefault or stopPropagation. The
 * former belongs to the backstop. The latter would be a no-op at window,
 * and on an element target would stop the event reaching a window-scoped
 * helper -- so an ambient "a file is being dragged" flag would go stale
 * exactly while the pointer is over a drop zone.
 */
export function dragHelper({
    target,
    preFilter = always,
    filter = always,
    dropEffect,
    onFirstDragEnter,
    onLastDragLeave,
    onDropAccepted,
    onDropRejected = onLastDragLeave,
    onDragEnd,
}: DragHelperOptions) {
    // dragenter/dragleave fire once per element the pointer crosses, not
    // once per drag, so the edges have to be found by counting depth rather
    // than taken from the events directly. Reset to 0 on drop and dragend:
    // neither is followed by a matching dragleave, so without that the
    // count only ever climbs and both edge callbacks go silent after the
    // first drop.
    let dragDepth = 0;

    const privateOnDragEnter = (e: DragEvent) => {
        if (preFilter(e)) {
            if (dragDepth++ === 0) {
                onFirstDragEnter?.(e);
            }
        }
    };

    const privateOnDragOver = (e: DragEvent) => {
        if (preFilter(e)) {
            if (dropEffect && e.dataTransfer) {
                e.dataTransfer.dropEffect = dropEffect;
            }
        }
    };

    const privateOnDragLeave = (e: DragEvent) => {
        if (!preFilter(e)) return;
        // Ignore an unmatched leave - registering mid-drag, or a preFilter
        // that answers differently on enter and leave, would otherwise
        // drive the count negative and swallow the next drag bringing it
        // back to 0.
        if (dragDepth === 0) return;
        if (--dragDepth === 0) {
            onLastDragLeave?.(e);
        }
    };

    const privateOnDrop = (e: DragEvent) => {
        dragDepth = 0;
        // Both branches must report something. A drop is not followed by a
        // dragleave, and dragend does not fire for drags that began outside
        // the page -- so a silently-dropped rejection would strand whatever
        // onFirstDragEnter set up, with no later event to undo it.
        if (filter(e)) {
            onDropAccepted?.(e);
        } else {
            onDropRejected?.(e);
        }
    };

    const privateOnDragEnd = (e: DragEvent) => {
        dragDepth = 0;
        onDragEnd?.(e);
    };

    target.addEventListener('dragenter', privateOnDragEnter);
    target.addEventListener('dragover', privateOnDragOver);
    target.addEventListener('dragleave', privateOnDragLeave);
    target.addEventListener('drop', privateOnDrop);
    target.addEventListener('dragend', privateOnDragEnd);

    return () => {
        target.removeEventListener('dragenter', privateOnDragEnter);
        target.removeEventListener('dragover', privateOnDragOver);
        target.removeEventListener('dragleave', privateOnDragLeave);
        target.removeEventListener('drop', privateOnDrop);
        target.removeEventListener('dragend', privateOnDragEnd);
    };
}

export function eventDataTransferHasFiles(event: DragEvent): boolean {
    return !!event.dataTransfer?.types.includes('Files');
}

export function eventDataTransferHasSingleFile(event: DragEvent): boolean {
    return (
        eventDataTransferHasFiles(event) &&
        event.dataTransfer?.files.length === 1
    );
}

export type FilePredicate = (file: File) => boolean;

export function makeSingleFileFilter(
    filePredicate?: FilePredicate
): DragEventPredicate {
    if (filePredicate) {
        return (event: DragEvent) =>
            eventDataTransferHasSingleFile(event) &&
            filePredicate(event.dataTransfer!.files[0]);
    } else {
        return eventDataTransferHasSingleFile;
    }
}
